/**
 * Chunk Load Error Recovery Utilities
 * 
 * Problem: After deploying a new build, users with cached index.html may try to load
 * old chunk files (e.g., chunk-XXXX.js) that no longer exist on the server, causing
 * "Failed to fetch dynamically imported module" errors.
 * 
 * Solution: Detect these errors and trigger a page reload to fetch the fresh index.html
 * with correct chunk references. We use sessionStorage to prevent infinite reload loops.
 */

/**
 * Detects if an error is a chunk load error by checking the error message
 * for common chunk load error patterns.
 * 
 * @param err - The error to check (can be Error, string, or object with message property)
 * @returns true if the error appears to be a chunk load error, false otherwise
 */
export function shouldReloadForChunkLoadError(err: unknown): boolean {
  if (!err) {
    return false;
  }

  let message = '';

  // Extract message from different error types
  if (err instanceof Error) {
    message = err.message || '';
  } else if (typeof err === 'string') {
    message = err;
  } else if (typeof err === 'object' && err !== null) {
    // Handle objects with message property
    const obj = err as { message?: string; error?: string; reason?: unknown };
    message = obj.message || obj.error || '';
    
    // Also check nested reason (common in promise rejections)
    if (!message && obj.reason) {
      return shouldReloadForChunkLoadError(obj.reason);
    }
  }

  // Normalize message to lowercase for case-insensitive matching
  const normalizedMessage = message.toLowerCase();

  // Check for common chunk load error patterns
  const chunkErrorPatterns = [
    'failed to fetch dynamically imported module',
    'loading chunk',
    'chunkloaderror',
    'importing a module script failed',
    'failed to fetch',
    'loading css chunk',
    'chunk load failed'
  ];

  return chunkErrorPatterns.some(pattern => normalizedMessage.includes(pattern));
}

/**
 * Safely reloads the page once to recover from chunk load errors.
 * Uses sessionStorage to prevent infinite reload loops.
 * 
 * @param storageKey - Optional custom key for sessionStorage (default: "__chunk_reload__")
 */
export function safeReloadOnce(storageKey: string = '__chunk_reload__'): void {
  try {
    // Check if we've already attempted a reload in this session
    const hasReloaded = sessionStorage.getItem(storageKey);
    
    if (hasReloaded) {
      // Already reloaded once, don't reload again to prevent infinite loop
      console.warn('[ChunkLoadRecover] Reload already attempted in this session. Aborting to prevent infinite loop.');
      return;
    }

    // Mark that we're about to reload
    sessionStorage.setItem(storageKey, 'true');
    
    console.info('[ChunkLoadRecover] Detected chunk load error. Reloading page to fetch fresh chunks...');
    
    // Reload the page to fetch fresh index.html with correct chunk references
    window.location.reload();
  } catch (error) {
    // Handle cases where sessionStorage is not available (e.g., Safari private mode)
    console.warn('[ChunkLoadRecover] sessionStorage not available. Attempting reload anyway...', error);
    
    // Still attempt reload even if sessionStorage fails
    // This is safe because browser navigation will reset sessionStorage state
    window.location.reload();
  }
}
